import { useCallback, useMemo, useRef, useState } from 'react';
import { useDisclosure, useColorMode } from '@chakra-ui/react';
import { useCalendarSettings, useFilters, useAutoContrastColor, styles } from '../../../../../lib';

export function useUnifiedFilterLogic({
	filterKey,
	options,
	isMulti,
	onApplyFilters,
}: {
	filterKey: string;
	options: string[];
	isMulti: boolean;
	onApplyFilters: () => void;
}) {
	const { setFilters } = useFilters();
	const { settings } = useCalendarSettings();
	const { colorMode } = useColorMode();
	const { color: contrastColor, ref } = useAutoContrastColor();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [search, setSearch] = useState('');
	const initialFocusRef = useRef<HTMLInputElement>(null);

	const filteredOptions = useMemo(() => {
		return options.filter((option) => option.toLowerCase().includes(search.toLowerCase()));
	}, [search, options]);

	const handleSearchChange = useCallback((e) => {
		setSearch(e.target.value);
	}, []);

	const handleChange = useCallback(
		(option: string) => {
			if (!isMulti) {
				setFilters((prev) => ({ ...prev, [filterKey]: [option] }));
				onClose();
				onApplyFilters();
				return;
			}
			setFilters((prev) => {
				const current = (prev[filterKey] as string[]) || [];
				const isSelected = current.includes(option);
				const updated = isSelected ? current.filter((item) => item !== option) : [...current, option];
				return { ...prev, [filterKey]: updated };
			});
			onApplyFilters();
		},
		[filterKey, isMulti, setFilters, onClose, onApplyFilters]
	);

	const handleOptionClick = useCallback(
		(option: string) => () => {
			handleChange(option);
		},
		[handleChange]
	);

	const handleClear = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			e.stopPropagation();
			setFilters((prev) => ({ ...prev, [filterKey]: [] }));
			onApplyFilters();
		},
		[filterKey, setFilters, onApplyFilters]
	);

	const handlePopoverClose = useCallback(() => {
		setSearch('');
		onClose();
	}, [onClose]);

	const { calendarBordersColor, calendarButtonsColor } = settings.styles[colorMode];
	const filterButtonHoverStyle = useMemo(() => ({ bg: calendarButtonsColor }), [calendarButtonsColor]);
	const filterOptionHoverStyle = useMemo(
		() => ({ bg: colorMode === 'light' ? 'gray.100' : 'gray.400' }),
		[colorMode]
	);

	const { filterPillResetHover, filterPopoverOutline } = styles.filters;

	return {
		ref,
		isOpen,
		onOpen,
		onClose: handlePopoverClose,
		filteredOptions,
		initialFocusRef,
		search,
		setSearch,
		handleSearchChange,
		handleClear,
		handleOptionClick,
		filterButtonHoverStyle,
		filterOptionHoverStyle,
		filterPillResetHover,
		filterPopoverOutline,
		colorMode,
		contrastColor,
		settings,
		calendarBordersColor,
	};
}
